// An asset's life as a vertical timeline, newest first (asset-detail-revamp-handover §3.8 + the owner's Lifecycle tab):
// a 28 px icon per kind of event, the title, then "when · source", joined by a hairline. The Overview shows the last
// few; the Lifecycle tab pages through all of it ("Show more", never a scroll area inside the page).
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import GridViewIcon from '@mui/icons-material/GridView';
import LabelOffIcon from '@mui/icons-material/LabelOff';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SellIcon from '@mui/icons-material/Sell';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WorkIcon from '@mui/icons-material/Work';
import WorkOffIcon from '@mui/icons-material/WorkOff';
import { Box, Button, Link, Skeleton, Stack, Typography } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import { useSession } from '../../auth/session';
import { type LifecycleItem, useAssetLifecycle } from '../../data/lifecycle';
import { calendarDate, dateTime } from '../../lib/time';

type Tone = 'success' | 'accent' | 'warning' | 'error' | 'neutral' | 'steel';

const TONE: Record<Tone, { fg: string; bg: string }> = {
  success: { fg: 'success.main', bg: 'success.main' },
  accent: { fg: 'primary.main', bg: 'primary.main' },
  warning: { fg: 'warning.main', bg: 'warning.main' },
  error: { fg: 'error.main', bg: 'error.main' },
  neutral: { fg: 'text.secondary', bg: 'text.secondary' },
  steel: { fg: 'secondary.main', bg: 'secondary.main' },
};

/** Icon and colour per kind (handover §3.8 table, mapped to the events this product records). */
export function lifecycleLook(item: LifecycleItem): { icon: SvgIconComponent; tone: Tone } {
  const p = item.params;
  switch (item.kind) {
    case 'location':
      switch (p.event) {
        case 'entered':
        case 'transit_end':
        case 'found':
          return { icon: LoginIcon, tone: 'success' };
        case 'transit_start':
          return { icon: LocalShippingIcon, tone: 'steel' };
        case 'missing':
          return { icon: ReportProblemIcon, tone: 'error' };
        case 'manual':
          return { icon: QrCodeScannerIcon, tone: 'neutral' };
        default:
          return { icon: LogoutIcon, tone: 'neutral' };
      }
    case 'transfer_dispatched':
    case 'transfer_received':
      return { icon: SwapHorizIcon, tone: 'accent' };
    case 'deployment_started':
      return { icon: WorkIcon, tone: 'accent' };
    case 'deployment_ended':
      return { icon: WorkOffIcon, tone: 'neutral' };
    case 'zone':
      return { icon: GridViewIcon, tone: 'neutral' };
    case 'counted':
      return {
        icon: FactCheckIcon,
        tone: p.bucket === 'not_found' || p.bucket === 'unexpected' ? 'warning' : 'neutral',
      };
    case 'registered':
      return { icon: AddCircleOutlineIcon, tone: 'steel' };
    case 'purchased':
      return { icon: ShoppingCartIcon, tone: 'neutral' };
    case 'tag_attached':
      return { icon: SellIcon, tone: 'neutral' };
    case 'tag_removed':
      return { icon: LabelOffIcon, tone: 'neutral' };
    case 'retired':
      return { icon: BlockIcon, tone: 'neutral' };
    case 'certificate_added':
      return { icon: VerifiedOutlinedIcon, tone: 'success' };
    case 'dispatch_override':
      return { icon: GppMaybeOutlinedIcon, tone: 'warning' };
    case 'condition':
      return {
        icon: HealthAndSafetyOutlinedIcon,
        tone:
          p.condition === 'unserviceable'
            ? 'error'
            : p.condition === 'poor'
              ? 'warning'
              : p.condition === 'good'
                ? 'success'
                : 'neutral',
      };
    case 'out_of_service':
      return { icon: BuildCircleOutlinedIcon, tone: 'warning' };
    case 'back_in_service':
      return { icon: PlayCircleOutlineIcon, tone: 'success' };
    case 'serviced':
      return { icon: HandymanOutlinedIcon, tone: item.source === 'repair' ? 'warning' : 'neutral' };
  }
}

type T = (key: string, o?: Record<string, unknown>) => string;

/** One line of text per event; a link to the record where there is one. */
export function lifecycleTitle(
  item: LifecycleItem,
  t: T,
  language = 'en',
): { text: string; to?: string } {
  const p = item.params;
  const s = (v: unknown) => (v == null || v === '' ? '—' : String(v));
  switch (item.kind) {
    case 'location':
      return { text: t(`event.${String(p.event)}`, { site: s(p.site), vehicle: s(p.vehicle) }) };
    case 'transfer_dispatched':
      return {
        text: t('lifecycle.k_transfer_dispatched', { ref: s(p.ref), from: s(p.from), to: s(p.to) }),
        to: `/transfers/${String(p.transfer_id)}`,
      };
    case 'transfer_received':
      return {
        text: t(
          p.status === 'not_received'
            ? 'lifecycle.k_transfer_not_received'
            : 'lifecycle.k_transfer_received',
          { ref: s(p.ref), to: s(p.to) },
        ),
        to: `/transfers/${String(p.transfer_id)}`,
      };
    case 'deployment_started':
      return {
        text: t('lifecycle.k_deployment_started', { project: s(p.project), site: s(p.site) }),
        to: `/projects/${String(p.project_id)}`,
      };
    case 'deployment_ended':
      return {
        text: t('lifecycle.k_deployment_ended', { project: s(p.project) }),
        to: `/projects/${String(p.project_id)}`,
      };
    case 'zone':
      return {
        text: p.to
          ? t('lifecycle.k_zone_in', { zone: s(p.to), site: s(p.site) })
          : t('lifecycle.k_zone_out', { zone: s(p.from), site: s(p.site) }),
      };
    case 'counted':
      return {
        text: t(`lifecycle.k_counted_${String(p.bucket)}`, { site: s(p.site) }),
        to: `/counts/${String(p.count_id)}`,
      };
    case 'registered':
      return {
        text: p.by ? t('lifecycle.k_registered_by', { by: p.by }) : t('lifecycle.k_registered'),
      };
    case 'purchased':
      return {
        text: [t('lifecycle.k_purchased', { supplier: s(p.supplier) }), p.po ? `PO ${p.po}` : null]
          .filter(Boolean)
          .join(' · '),
      };
    case 'tag_attached':
      return {
        text: t('lifecycle.k_tag_attached', {
          type: t(`reports.tag_type_${String(p.type)}`),
          tag: s(p.tag),
        }),
      };
    case 'tag_removed':
      return { text: t('lifecycle.k_tag_removed', { by: s(p.by) }) };
    case 'retired':
      return {
        text: [
          t('lifecycle.k_retired', {
            reason: p.reason ? t(`retire.reason_${String(p.reason)}`) : t('retire.reason_unknown'),
          }),
          p.note ? String(p.note) : null,
        ]
          .filter(Boolean)
          .join(' — '),
      };
    case 'certificate_added':
      return {
        text: [
          t('lifecycle.k_certificate_added', {
            kind: s(p.kind),
            date:
              calendarDate(typeof p.expires_on === 'string' ? p.expires_on : null, language) ?? '—',
          }),
          p.by ? String(p.by) : null,
        ]
          .filter(Boolean)
          .join(' · '),
      };
    case 'dispatch_override':
      return {
        text: [
          t('lifecycle.k_dispatch_override', { ref: s(p.ref), reason: s(p.reason) }),
          p.by ? String(p.by) : null,
        ]
          .filter(Boolean)
          .join(' · '),
        to: `/transfers/${String(p.transfer_id)}`,
      };
    case 'condition':
      return {
        text: [
          t('lifecycle.k_condition', {
            condition: t(`condition.${String(p.condition)}`),
            source: t(`condition.src_${String(item.source)}`),
          }),
          p.note ? `“${String(p.note)}”` : null,
          p.by ? String(p.by) : null,
        ]
          .filter(Boolean)
          .join(' · '),
      };
    case 'out_of_service':
      return {
        text: [t('lifecycle.k_out_of_service', { reason: s(p.reason) }), p.by ? String(p.by) : null]
          .filter(Boolean)
          .join(' · '),
      };
    case 'back_in_service':
      return {
        text: [t('lifecycle.k_back_in_service'), p.by ? String(p.by) : null]
          .filter(Boolean)
          .join(' · '),
      };
    case 'serviced':
      // the work only — its cost is managers' and never in the feed (L19)
      return {
        text: [
          t('lifecycle.k_serviced', {
            kind: t(`svc.kind_${String(item.source)}`),
            task: p.task ? String(p.task) : t('svc.no_task'),
          }),
          p.supplier ? String(p.supplier) : null,
          p.by ? String(p.by) : null,
        ]
          .filter(Boolean)
          .join(' · '),
      };
  }
}

function Row({ item, last }: { item: LifecycleItem; last: boolean }) {
  const { t, i18n } = useTranslation();
  const { me } = useSession();
  const tz = me?.company?.timezone ?? 'Asia/Kuala_Lumpur';
  const { icon: Icon, tone } = lifecycleLook(item);
  const { text, to } = lifecycleTitle(item, t, i18n.language);
  const source = item.kind === 'location' && item.source ? t(`source.${item.source}`) : null;
  const title: ReactNode = to ? (
    <Link component={RouterLink} to={to} color="inherit" underline="hover">
      {text}
    </Link>
  ) : (
    text
  );
  return (
    <Stack direction="row" spacing={1.5} data-testid={`lifecycle-${item.kind}`}>
      <Stack alignItems="center" sx={{ width: 28, flex: 'none' }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            color: TONE[tone].fg,
            position: 'relative',
            overflow: 'hidden',
            // a soft disc of the tone behind the icon
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              bgcolor: TONE[tone].bg,
              opacity: 0.12,
            },
          }}
        >
          <Icon sx={{ fontSize: 16, position: 'relative' }} />
        </Box>
        {!last ? <Box sx={{ flex: 1, width: '1px', bgcolor: 'divider', my: 0.5 }} /> : null}
      </Stack>
      <Box sx={{ pb: last ? 0 : 2, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {[dateTime(item.at, i18n.language, tz, { year: true }), source]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
      </Box>
    </Stack>
  );
}

/**
 * `pageSize` rows; `more` = the Lifecycle tab's "Show more" (fetches the next page), `footer` = what the Overview puts
 * under its few rows (a link to the tab).
 */
export function LifecycleFeed({
  assetId,
  pageSize,
  more = false,
  footer,
}: {
  assetId: string;
  pageSize: number;
  more?: boolean;
  footer?: ReactNode;
}) {
  const { t } = useTranslation();
  const feed = useAssetLifecycle(assetId, pageSize);
  const items = feed.data?.pages.flat() ?? [];
  if (feed.isLoading) {
    return (
      <Stack spacing={1.5}>
        {[0, 1, 2].map((i) => (
          <Stack key={i} direction="row" spacing={1.5} alignItems="center">
            <Skeleton variant="circular" width={28} height={28} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="45%" />
              <Skeleton width="25%" />
            </Box>
          </Stack>
        ))}
      </Stack>
    );
  }
  if (feed.error) {
    return (
      <Typography color="error" variant="body2">
        {t('common.error_generic')}{' '}
        <Button size="small" onClick={() => void feed.refetch()}>
          {t('common.retry')}
        </Button>
      </Typography>
    );
  }
  if (!items.length) return <Typography color="text.secondary">{t('lifecycle.empty')}</Typography>;
  return (
    <Box data-testid="lifecycle-feed">
      {items.map((item, i) => (
        <Row key={item.key + item.at} item={item} last={i === items.length - 1} />
      ))}
      {more && feed.hasNextPage ? (
        <Button
          sx={{ mt: 1 }}
          onClick={() => void feed.fetchNextPage()}
          disabled={feed.isFetchingNextPage}
          data-testid="lifecycle-more"
        >
          {t('lifecycle.more')}
        </Button>
      ) : null}
      {footer}
    </Box>
  );
}
